import React, { useState, useRef } from 'react';
import { Calendar, Clock, MapPin, Users, CheckCircle2, Loader2, X, Send, Type, Video as VideoIcon } from 'lucide-react';
import { supabase, supabase_save_candidate_communication, supabase_merge_application_pipeline_meta } from '../services/supabaseService';
import AiRichTextEditor from './AiRichTextEditor';
import { createTeamsMeeting } from '../services/microsoftGraphService';

const TIME_SLOT_STEP_MIN = 15;
const DEFAULT_MEETING_DURATION_MIN = 30;

const TIME_SLOTS: string[] = Array.from({ length: 24 * 4 }, (_, i) =>
    `${String(Math.floor(i / 4)).padStart(2, '0')}:${String((i % 4) * TIME_SLOT_STEP_MIN).padStart(2, '0')}`
);

const timeToMinutes = (t: string): number => {
    const [hRaw, mRaw = '0'] = t.trim().split(':');
    const h = Math.min(23, Math.max(0, parseInt(hRaw, 10) || 0));
    const m = Math.min(59, Math.max(0, parseInt(String(mRaw).slice(0, 2), 10) || 0));
    return h * 60 + m;
};

const minutesToTime = (mins: number): string => {
    const capped = Math.min(23 * 60 + 45, Math.max(0, mins));
    const h = Math.floor(capped / 60);
    const m = capped % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const addMinutesToTime = (t: string, deltaMin: number): string =>
    minutesToTime(timeToMinutes(t) + deltaMin);

interface TeamsMeetingSchedulerProps {
    onClose: () => void;
    onScheduled: (meetingDetails: any) => void;
    clientId?: string;
    vacancyId?: string;
    /** Kandidaat-id (candidates.id) — verplicht voor interviews + logging. */
    candidateId?: string;
    /** Sollicitatie-id (applications.id) — voor gesprek-datum op Kanban/shortlist (cv_snapshot.pipeline_meta). */
    applicationId?: string;
    stageId?: string;
    initialDate?: string; // YYYY-MM-DD
}

const TeamsMeetingScheduler: React.FC<TeamsMeetingSchedulerProps> = ({ onClose, onScheduled, clientId, vacancyId, candidateId, applicationId, stageId, initialDate }) => {
    const [companyUsers, setCompanyUsers] = useState<any[]>([]);
    const [title, setTitle] = useState('');
    const [date, setDate] = useState(initialDate || new Date().toISOString().split('T')[0]);
    const [startTime, setStartTime] = useState('10:00');
    const [endTime, setEndTime] = useState('10:30');
    const [duration, setDuration] = useState('30m');
    const [allDay, setAllDay] = useState(false);
    const [location, setLocation] = useState('');
    const [isOnline, setIsOnline] = useState(true);
    const [details, setDetails] = useState('');
    
    // States for template shortcodes
    const [candidateName, setCandidateName] = useState('');
    const [vacancyTitle, setVacancyTitle] = useState('');
    const [clientName, setClientName] = useState('');
    const [userSignature, setUserSignature] = useState('');

    const [attendees, setAttendees] = useState<string[]>([]);
    const [attendeeInput, setAttendeeInput] = useState('');
    const [showMentionMenu, setShowMentionMenu] = useState(false);
    const mentionInputRef = useRef<HTMLInputElement>(null);

    const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [errorMessage, setErrorMessage] = useState('');
    const [templates, setTemplates] = useState<any[]>([]);

    React.useEffect(() => {
        const fetchTeam = async () => {
            if (clientId && clientId !== 'system' && clientId !== '00000000-0000-0000-0000-000000000001') {
                const { data } = await supabase.from('profiles').select('name, email').eq('client_id', clientId);
                if (data) setCompanyUsers(data);
            }
        };
        const fetchCandidateAndVacancy = async () => {
            if (candidateId) {
                const { data } = await supabase.from('candidates').select('email, name').eq('id', candidateId).single();
                if (data) {
                    if (data.email && !attendees.includes(data.email)) {
                        setAttendees(prev => [...prev, data.email]);
                    }
                    setCandidateName(data.name || '');
                }
            }
            const isValidUUID = (id: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id);
            if (vacancyId && isValidUUID(vacancyId)) {
                const { data } = await supabase.from('vacancies').select('title, client_id').eq('id', vacancyId).single();
                if (data) {
                    setVacancyTitle(data.title || '');
                    if (data.client_id) {
                        const { data: clientData } = await supabase.from('clients').select('name').eq('id', data.client_id).single();
                        if (clientData) setClientName(clientData.name || '');
                    }
                }
            }
        };
        const fetchExtra = async () => {
            const { data: userDat } = await supabase.auth.getUser();
            if (userDat?.user) {
                const { data: prof } = await supabase.from('profiles').select('client_id').eq('id', userDat.user.id).single();
                if (prof?.client_id) {
                    const { data: clientData } = await supabase.from('clients').select('data').eq('id', prof.client_id).single();
                    
                    let sig = '';
                    if (clientData?.data?.user_signatures && clientData.data.user_signatures[userDat.user.id]) {
                        sig = clientData.data.user_signatures[userDat.user.id];
                    } else if (clientData?.data?.email_signature) {
                        sig = clientData.data.email_signature;
                    }
                    
                    if (sig) {
                        setUserSignature(sig);
                        if (!details.includes(sig)) {
                            setDetails(prev => prev ? `${prev}<br><br>${sig}` : `<br><br>${sig}`);
                        }
                    }

                    // Fix: email templates reside in client JSON 'data' field, not in a separate table.
                    if (clientData?.data?.email_templates) {
                        setTemplates(clientData.data.email_templates);
                    }
                }
            }
        };
        fetchTeam();
        fetchCandidateAndVacancy();
        fetchExtra();
    }, [clientId, candidateId]);

    React.useEffect(() => {
        if (initialDate && /^\d{4}-\d{2}-\d{2}$/.test(initialDate)) {
            setDate(initialDate);
        }
    }, [initialDate]);

    React.useEffect(() => {
        if (timeToMinutes(endTime) <= timeToMinutes(startTime)) {
            setEndTime(addMinutesToTime(startTime, DEFAULT_MEETING_DURATION_MIN));
        }
    }, [startTime]);

    const endTimeOptions = TIME_SLOTS.filter(t => timeToMinutes(t) > timeToMinutes(startTime));

    const handleStartTimeChange = (newStart: string) => {
        setStartTime(newStart);
        if (timeToMinutes(endTime) <= timeToMinutes(newStart)) {
            setEndTime(addMinutesToTime(newStart, DEFAULT_MEETING_DURATION_MIN));
        }
        if (status === 'error' && errorMessage.includes('Eindtijd')) {
            setStatus('idle');
            setErrorMessage('');
        }
    };

    const handleEndTimeChange = (newEnd: string) => {
        if (timeToMinutes(newEnd) <= timeToMinutes(startTime)) {
            setErrorMessage('Eindtijd moet na de starttijd liggen.');
            setStatus('error');
            return;
        }
        setEndTime(newEnd);
        if (status === 'error' && errorMessage.includes('Eindtijd')) {
            setStatus('idle');
            setErrorMessage('');
        }
    };

    const splitEmails = (raw: string): string[] => {
        return raw
            .split(/[,\s;]+/g)
            .map(s => s.trim())
            .filter(Boolean);
    };

    const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const commitAttendeeInput = () => {
        const tokens = splitEmails(attendeeInput);
        if (tokens.length === 0) return { added: 0, invalid: 0 };
        const valid = tokens.filter(isValidEmail);
        const invalid = tokens.filter(t => !isValidEmail(t));
        if (valid.length > 0) {
            setAttendees(prev => {
                const set = new Set(prev);
                valid.forEach(v => set.add(v));
                return Array.from(set);
            });
        }
        setAttendeeInput('');
        setShowMentionMenu(false);
        return { added: valid.length, invalid: invalid.length };
    };

    const handleAttendeeInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setAttendeeInput(val);
        // Only show mention menu when there are actually matching internal users
        const q = val.trim().toLowerCase();
        if (!q) return setShowMentionMenu(false);
        const hasMatches = companyUsers.some(u =>
            (u.name && u.name.toLowerCase().includes(q)) ||
            (u.email && u.email.toLowerCase().includes(q))
        );
        setShowMentionMenu(hasMatches);
    };

    const handleSelectMention = (user: any) => {
        const email = user.email;
        if (!attendees.includes(email)) {
            setAttendees([...attendees, email]);
        }
        setAttendeeInput('');
        setShowMentionMenu(false);
        mentionInputRef.current?.focus();
    };

    const handleAddAttendeeManual = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== 'Enter') return;
        if (!attendeeInput.trim()) return;
        // Always allow Enter to commit typed emails (also supports "a@b.com; c@d.com")
        e.preventDefault();
        commitAttendeeInput();
    };

    const removeAttendee = (email: string) => {
        setAttendees(attendees.filter(e => e !== email));
    };

    const handleSubmit = async () => {
        if (!title.trim()) {
            setErrorMessage("Voeg een titel toe voor de afspraak.");
            setStatus('error');
            return;
        }

        setStatus('loading');
        setErrorMessage('');

        try {
            // If user typed an extra email but didn't press Enter, commit it now.
            const committed = commitAttendeeInput();
            if (committed.invalid > 0) {
                setErrorMessage("Een of meer e-mailadressen zijn ongeldig. Corrigeer ze en probeer opnieuw.");
                setStatus('error');
                return;
            }

            const padHm = (t: string) => {
                const [hRaw, mRaw = '0'] = t.trim().split(':');
                const h = Math.min(23, Math.max(0, parseInt(hRaw, 10) || 0));
                const m = Math.min(59, Math.max(0, parseInt(String(mRaw).slice(0, 2), 10) || 0));
                return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
            };
            const hmStart = padHm(startTime);
            const hmEnd = padHm(endTime);
            if (timeToMinutes(hmEnd) <= timeToMinutes(hmStart)) {
                setErrorMessage('Eindtijd moet na de starttijd liggen.');
                setStatus('error');
                return;
            }
            const startDateTime = new Date(`${date}T${hmStart}:00`).toISOString();
            const endDateTime = new Date(`${date}T${hmEnd}:00`).toISOString();

            const attendeeObjects = attendees.map(email => ({ email, name: email }));

            const saveInterviewRecord = async (finalUrl: string | null, finalLocation: string) => {
                const isValidUUID = (id: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id);
                if (!vacancyId || !candidateId) return;
                // In global pipelines vacancyId can be ALL_OPEN/ALL_GESLOTEN. In that case we still create the calendar event,
                // but skip writing an interview record to Supabase.
                if (!isValidUUID(vacancyId) || !isValidUUID(candidateId)) return;

                let mins = 30;
                if (duration.includes('m')) mins = parseInt(duration) || 30;
                else if (duration.includes('h')) mins = (parseInt(duration) || 1) * 60;

                const finalStageId = stageId && isValidUUID(stageId) ? stageId : null;

                try {
                    await supabase.from('interviews').insert({
                        vacancy_id: vacancyId,
                        candidate_id: candidateId,
                        stage_id: finalStageId,
                        scheduled_at: startDateTime,
                        duration_minutes: mins,
                        location: finalLocation,
                        video_link: finalUrl,
                        interviewers: attendees,
                        status: 'gepland'
                    });

                    const dayIso = date;
                    if (applicationId) {
                        await supabase_merge_application_pipeline_meta(applicationId, {
                            gesprek_date: dayIso,
                            gesprek_time_start: hmStart,
                            gesprek_time_end: hmEnd,
                            gesprek_scheduled_at: startDateTime,
                        });
                    }

                    const { data: userData } = await supabase.auth.getUser();
                    if (userData?.user) {
                        let logMsg = `Interview gepland op ${new Date(startDateTime).toLocaleString('nl-NL', { timeZone: 'Europe/Amsterdam' })} (${mins} min).\nLocatie: ${finalLocation || 'Niet opgegeven'}`;
                        if (attendees.length > 0) logMsg += `\nMet: ${attendees.join(', ')}`;
                        const comm = await supabase_save_candidate_communication(candidateId, userData.user.id, logMsg, 'note');
                        if (!comm) {
                            console.warn('[TeamsMeetingScheduler] candidate_communications insert failed (check RLS); interview + pipeline_meta may still be saved.');
                        }
                    }
                } catch (e) {
                    console.error("Failed to save interview record to DB", e);
                }
            };

            if (isOnline) {
                const { joinUrl, error: msError } = await createTeamsMeeting(
                    title,
                    startDateTime,
                    endDateTime,
                    attendeeObjects,
                    { bodyContent: details }
                );

                if (msError) {
                    console.error("Teams Error:", msError);
                    if (msError.toString().includes('Microsoft authentication required')) {
                        await supabase.auth.signOut();
                        window.location.href = '/login';
                        return;
                    }
                    setErrorMessage("Er ging iets mis bij het aanmaken van de Teams meeting via Microsoft Graph.");
                    setStatus('error');
                } else {
                    await saveInterviewRecord(joinUrl, location);
                    setStatus('success');
                    window.dispatchEvent(
                        new CustomEvent('wt:toast', { detail: { message: 'Interview succesvol ingepland!', type: 'success' } }),
                    );
                    setTimeout(() => {
                        onScheduled({ title, start: startDateTime, end: endDateTime, url: joinUrl });
                        onClose();
                    }, 2000);
                }
            } else {
                await saveInterviewRecord(null, location);
                setStatus('success');
                window.dispatchEvent(
                    new CustomEvent('wt:toast', { detail: { message: 'Interview succesvol ingepland!', type: 'success' } }),
                );
                setTimeout(() => {
                    onScheduled({ title, start: startDateTime, end: endDateTime, location });
                    onClose();
                }, 2000);
            }

        } catch (e) {
            console.error(e);
            setErrorMessage("Fout bij plannen vanuit Teams Modal");
            setStatus('error');
        }
    };

    return (
        <div className="fixed inset-0 z-[300] flex justify-end bg-slate-900/40 backdrop-blur-md overflow-hidden animate-in fade-in duration-300">
            <div className="bg-white w-full max-w-2xl h-full shadow-2xl flex flex-col relative animate-in slide-in-from-right duration-500 border-l border-white/20">
                
                {/* SUCCESS OVERLAY */}
                {status === 'success' && (
                    <div className="absolute inset-0 z-50 bg-white/95 backdrop-blur-sm flex flex-col items-center justify-center animate-fadeIn">
                        <div className="w-24 h-24 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-500 mb-6 animate-bounce">
                            <CheckCircle2 size={48} />
                        </div>
                        <h2 className="text-3xl font-black text-slate-800 tracking-tight">Afspraak Gepland!</h2>
                        <p className="text-slate-500 mt-2 font-medium text-center max-w-md px-4">
                            {attendees.length === 0
                                ? 'De afspraak staat in je eigen Outlook-agenda. Je hebt geen genodigden toegevoegd: Outlook stuurt dan geen aparte uitnodigingsmail (ook niet naar jezelf). Voeg minimaal je eigen werk-mail toe als je een e-mailbevestiging wilt.'
                                : 'De uitnodiging is succesvol verwerkt en naar de genodigden verstuurd.'}
                        </p>
                    </div>
                )}

                {/* ERROR BANNER */}
                {status === 'error' && (
                    <div className="bg-red-500 text-white px-6 py-4 flex items-center justify-between z-40">
                        <div className="flex items-center gap-3">
                            <X size={20} />
                            <span className="font-bold text-sm">{errorMessage}</span>
                        </div>
                        <button onClick={() => setStatus('idle')} className="p-1 hover:bg-white/20 rounded-full transition-colors"><X size={16}/></button>
                    </div>
                )}

                {/* Header Navbar */}
                <div className="flex items-center justify-between px-8 py-6 border-b border-slate-100 bg-white z-30">
                    <div className="flex items-center gap-5">
                        <div className="w-14 h-14 bg-gradient-to-br from-[#5b5fc7] to-indigo-500 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-[#5b5fc7]/30">
                            <Calendar size={26} />
                        </div>
                        <div>
                            <h2 className="text-2xl font-black text-slate-900 tracking-tight leading-tight">Nieuwe Afspraak</h2>
                            <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mt-1">Microsoft Teams of Fysiek</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2.5 text-slate-400 hover:text-slate-700 bg-slate-50 hover:bg-slate-100 rounded-full transition-all">
                        <X size={20} />
                    </button>
                </div>

                <div className="flex-1 flex flex-col bg-[#f8fafc] overflow-y-auto custom-scrollbar">
                    <div className="p-8 space-y-6 flex-1">

                        {/* Title Input */}
                        <div>
                            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block ml-1">Onderwerp</label>
                            <div className="bg-white rounded-[1.25rem] shadow-sm border border-slate-200 focus-within:ring-4 focus-within:ring-[#5b5fc7]/20 focus-within:border-[#5b5fc7] transition-all flex text-lg px-2">
                                <div className="p-4 text-[#5b5fc7]"><Type size={20} /></div>
                                <input
                                    type="text"
                                    placeholder="Bijv. Kennismaking - Jan Jansen"
                                    className="w-full bg-transparent border-none outline-none py-4 pr-4 text-slate-800 placeholder-slate-300 font-bold"
                                    value={title}
                                    onChange={e => setTitle(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Date / Time */}
                            <div className="space-y-4">
                                <div>
                                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block ml-1">Wanneer</label>
                                    <div className="bg-white rounded-[1.25rem] shadow-sm border border-slate-200 p-2 flex flex-col gap-2">
                                        <div className="flex items-center gap-2 px-3 py-1">
                                            <Calendar size={16} className="text-slate-400" />
                                            <input type="date" value={date} onChange={e => setDate(e.target.value)} className="flex-1 p-2 border-none bg-slate-50 hover:bg-slate-100 rounded-xl outline-none cursor-pointer font-bold text-slate-700 transition-colors" />
                                        </div>
                                        <div className="flex items-center gap-2 px-3 pb-1">
                                            <Clock size={16} className="text-slate-400" />
                                            <select value={startTime} onChange={e => handleStartTimeChange(e.target.value)} className="flex-1 p-2 border-none bg-slate-50 hover:bg-slate-100 rounded-xl outline-none cursor-pointer font-bold text-slate-700 transition-colors">
                                                {TIME_SLOTS.map(t => (
                                                    <option key={t} value={t}>{t}</option>
                                                ))}
                                            </select>
                                            <span className="text-slate-300 font-black">-</span>
                                            <select value={endTimeOptions.includes(endTime) ? endTime : endTimeOptions[0]} onChange={e => handleEndTimeChange(e.target.value)} className="flex-1 p-2 border-none bg-slate-50 hover:bg-slate-100 rounded-xl outline-none cursor-pointer font-bold text-slate-700 transition-colors">
                                                {endTimeOptions.map(t => (
                                                    <option key={t} value={t}>{t}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Location & Online */}
                            <div className="space-y-4">
                                <div>
                                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block ml-1">Locatie</label>
                                    <div className="bg-white rounded-[1.25rem] shadow-sm border border-slate-200 p-2 flex flex-col gap-2 h-[104px] justify-center">
                                        
                                        <div className="flex items-center justify-between px-4 py-2 bg-slate-50 rounded-xl border border-slate-100">
                                            <span className="text-sm font-bold text-slate-700 flex items-center gap-2"><VideoIcon size={16} className="text-[#5b5fc7]" /> Online (Teams)</span>
                                            <div onClick={() => setIsOnline(!isOnline)} className={`w-12 h-6 rounded-full flex items-center p-1 cursor-pointer transition-colors ${isOnline ? 'bg-[#5b5fc7]' : 'bg-slate-300'}`}>
                                                <div className={`bg-white w-4 h-4 rounded-full shadow-sm transform transition-transform ${isOnline ? 'translate-x-6' : 'translate-x-0'}`}></div>
                                            </div>
                                        </div>

                                        {!isOnline && (
                                            <div className="flex items-center gap-2 px-3 animate-fadeIn">
                                                <MapPin size={16} className="text-slate-400" />
                                                <input
                                                    type="text"
                                                    placeholder="Adres of Kantoor"
                                                    value={location}
                                                    onChange={e => setLocation(e.target.value)}
                                                    className="flex-1 p-1.5 border-none outline-none font-bold text-slate-700 bg-transparent placeholder-slate-300"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Attendees Input */}
                        <div>
                            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block ml-1">Genodigden</label>
                            <div className="bg-white rounded-[1.25rem] shadow-sm border border-slate-200 focus-within:ring-4 focus-within:ring-[#5b5fc7]/20 focus-within:border-[#5b5fc7] flex min-h-[64px] relative px-2 transition-all">
                                <div className="p-4 text-[#5b5fc7]"><Users size={20} /></div>
                                <div className="flex-1 flex flex-wrap items-center py-3 gap-2">
                                    {attendees.map(email => (
                                        <span key={email} className="bg-indigo-50 text-indigo-700 border border-indigo-100 px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-2 shadow-sm">
                                            {email}
                                            <button onClick={() => removeAttendee(email)} className="hover:bg-indigo-200 p-0.5 rounded-full transition-colors"><X size={12} /></button>
                                        </span>
                                    ))}
                                    <input
                                        ref={mentionInputRef}
                                        type="text"
                                        placeholder={attendees.length === 0 ? "E-mailadressen toevoegen..." : ""}
                                        className="bg-transparent border-none outline-none flex-1 min-w-[200px] text-sm text-slate-800 placeholder-slate-300 font-bold"
                                        value={attendeeInput}
                                        onChange={handleAttendeeInputChange}
                                        onKeyDown={handleAddAttendeeManual}
                                    />
                                </div>

                                {showMentionMenu && (
                                    <div className="absolute bottom-full left-10 mb-2 w-80 bg-white border border-slate-200 rounded-2xl shadow-2xl z-50 overflow-hidden max-h-64 overflow-y-auto">
                                        {companyUsers.filter(u => u.name?.toLowerCase().includes(attendeeInput.toLowerCase()) || u.email?.toLowerCase().includes(attendeeInput.toLowerCase())).map(u => (
                                            <button
                                                key={u.email}
                                                type="button"
                                                onClick={() => handleSelectMention(u)}
                                                className="w-full text-left px-5 py-3 hover:bg-slate-50 border-b border-slate-50 text-sm font-bold text-slate-800 flex flex-col transition-colors"
                                            >
                                                <span>{u.name || 'Naamloos'}</span>
                                                <span className="text-[11px] text-slate-400 font-semibold">{u.email}</span>
                                            </button>
                                        ))}
                                        {companyUsers.filter(u => u.name?.toLowerCase().includes(attendeeInput.toLowerCase()) || u.email?.toLowerCase().includes(attendeeInput.toLowerCase())).length === 0 && (
                                            <div className="px-5 py-4 text-xs text-slate-400 italic text-center font-medium">Druk op Enter om toe te voegen</div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <p className="text-[10px] text-slate-500 mt-2 ml-1 leading-snug">
                                Tijden worden in <strong>Europe/Amsterdam</strong> naar Outlook gestuurd. Zonder genodigden verschijnt de meeting alleen in je agenda — voeg e-mailadressen toe voor uitnodigingsmail.
                            </p>
                        </div>

                        {/* Rich Text Details */}
                        <div>
                            <div className="flex items-center justify-between mb-2 ml-1">
                                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block">Bericht / Uitnodiging</label>
                                {templates.length > 0 && (
                                    <select
                                        className="text-[10px] font-black uppercase tracking-widest text-[#5b5fc7] bg-indigo-50 hover:bg-indigo-100 rounded-lg px-3 py-1.5 outline-none cursor-pointer border-none transition-colors"
                                        onChange={(e) => {
                                            const t = templates.find(x => x.id === e.target.value);
                                            if (t) {
                                                let processedBody = t.body;
                                                processedBody = processedBody.replace(/\[NAAM\]/gi, candidateName || '[Naam]');
                                                processedBody = processedBody.replace(/\[FUNCTIE\]/gi, vacancyTitle || '[Functie]');
                                                processedBody = processedBody.replace(/\[VACATURE\]/gi, vacancyTitle || '[Vacature]');
                                                processedBody = processedBody.replace(/\[BEDRIJF\]/gi, clientName || '[Bedrijf]');
                                                processedBody = processedBody.replace(/\[HANDTEKENING\]/gi, userSignature);
                                                
                                                // Als template een handtekening shortcode had, haal de al bestaande handtekening uit 'details' weg
                                                // zodat we m niet dubbel hebben (als we die in de body zetten).
                                                let currentDetails = details;
                                                if (t.body.includes('[HANDTEKENING]') && userSignature && currentDetails.includes(userSignature)) {
                                                    currentDetails = currentDetails.replace(`<br><br>${userSignature}`, '');
                                                    currentDetails = currentDetails.replace(userSignature, '');
                                                }

                                                setDetails(processedBody + '<br><br>' + currentDetails);
                                            }
                                        }}
                                        defaultValue=""
                                    >
                                        <option value="" disabled>Sjabloon inladen...</option>
                                        {templates.map(t => (
                                            <option key={t.id} value={t.id}>{t.name}</option>
                                        ))}
                                    </select>
                                )}
                            </div>
                            <div className="bg-white rounded-[1.25rem] shadow-sm border border-slate-200 overflow-hidden focus-within:ring-4 focus-within:ring-[#5b5fc7]/20 focus-within:border-[#5b5fc7] transition-all">
                                <AiRichTextEditor
                                    value={details}
                                    onChange={setDetails}
                                    placeholder="Typ hier de uitnodigingstekst voor de kandidaat..."
                                    minHeight="250px"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="p-6 bg-white border-t border-slate-200 flex justify-end gap-3 shrink-0 z-30 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] relative">
                        <button onClick={onClose} className="px-6 py-3.5 text-slate-500 font-bold text-sm rounded-xl hover:bg-slate-200 transition-colors">
                            Annuleren
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={status === 'loading'}
                            className="bg-[#5b5fc7] hover:bg-indigo-700 text-white px-8 py-3.5 rounded-xl text-sm font-black shadow-lg shadow-indigo-500/30 transition-all flex items-center gap-2"
                        >
                            {status === 'loading' ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                            Uitnodiging Versturen
                        </button>
                    </div>
            </div>
        </div>
    );
};

export default TeamsMeetingScheduler;
